import { BORDER_GRAY } from './editorAccents';
import { PANEL_HEADING_STYLE } from './panelHeading';

/**
 * Les vignettes de la palette d'éléments, telles que le classique les dessine
 * dans son panneau de gauche : la palette du web-editor lit les mêmes valeurs.
 */

export const PALETTE_TILE_ICON_SIZE = 25;

// Grille 3 colonnes dans 270 px : ~74 px de large par vignette.
export const PALETTE_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 8,
  alignItems: 'stretch',
};

// Teinte de survol OPAQUE : la vignette peut être posée sur un fond foncé
// (modal navy « Ajouter une ligne ») où un survol translucide rendrait l'icône
// illisible.
const TILE_HOVER_BG = '#EDEFF5';

// Lissage par défaut du navigateur : Mantine pose `antialiased` sur le body du
// web-editor, ce qui affine le texte sous macOS ; le classique n'en pose pas.
const DEFAULT_FONT_SMOOTHING = {
  WebkitFontSmoothing: 'auto',
  MozOsxFontSmoothing: 'auto',
};

export const paletteTileStyle = ({ background, hovered, accentColor, color = '#000000' }) => ({
  width: '100%',
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  backgroundColor: hovered ? TILE_HOVER_BG : background,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: hovered ? accentColor : BORDER_GRAY,
  borderRadius: 5,
  fontFamily: 'Montserrat',
  ...DEFAULT_FONT_SMOOTHING,
  color: hovered ? accentColor : color,
  textAlign: 'center',
  boxShadow: hovered ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none',
  transition: 'border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
});

// Contenu de la vignette : icône au-dessus du libellé. Padding horizontal
// réduit : chaque pixel rendu au texte évite de descendre d'un cran de police.
export const PALETTE_TILE_BODY_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '4px 2px',
};

// Un libellé d'un seul mot long (« Organigramme », 86 px à 11 px) n'a aucun
// espace où passer à la ligne : la césure automatique le coupe proprement.
// `hyphens: auto` exige l'attribut lang sur l'élément.
// La police système d'antd : dans le classique, le Button qui porte le
// libellé l'impose par-dessus le Montserrat de la vignette.
const ANTD_SYSTEM_FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

export const PALETTE_TILE_LABEL_STYLE = {
  fontFamily: ANTD_SYSTEM_FONT,
  fontSize: 11,
  lineHeight: 1.15,
  fontWeight: 500,
  maxWidth: '100%',
  hyphens: 'auto',
  // Filet de sécurité sans dictionnaire de césure : coupure sans tiret plutôt
  // que débordement.
  overflowWrap: 'anywhere',
};

// Titre de groupe (« Contenu », « Données »…) centré entre deux traits : le
// libellé porte la couleur de la famille, les traits l'accompagnent (suffixe
// hexadécimal d'opacité).
const SUBTLE_TEXT_COLOR = '#9aa1ad';
const SUBTLE_LINE_COLOR = '#c3c8d1';
const ACCENT_LINE_ALPHA = '59';
const STRONG_ACCENT_LINE_ALPHA = '99';

export const paletteGroupHeadingStyle = (accent) => ({
  ...PANEL_HEADING_STYLE,
  fontFamily: 'Montserrat',
  ...DEFAULT_FONT_SMOOTHING,
  fontSize: 10,
  color: accent ?? SUBTLE_TEXT_COLOR,
});

export const paletteGroupLineColor = (accent, { strong = false } = {}) => {
  if (!accent) return SUBTLE_LINE_COLOR;
  return `${accent}${strong ? STRONG_ACCENT_LINE_ALPHA : ACCENT_LINE_ALPHA}`;
};
